// Naming convention for flags: <app-name>.<page-name>.<flag-name> use hyphens for page-name and flag-name

export type CC9FlagNames =
  | "css.enable_dealer_code_service_use"
  | "cc9.test.testflag"
  | "cc9-client-search.show-download"
  | "cc9.account-copy.show"
  | "cc9.user-permissions-from-group-resource"
  | "cc9.get-legacy-data"
  | "cc9.resources-hide-ui"
  | "cc9.website-inventory-config.show-bulk-import"
  | "cc9.user-permissions-manager.enable"
  | "incentives-manager-access"
  | "cc9.media-library-copy.enable"
  | "css.franchise-management.enable"
  | "cis.account-creation.enable"
  | "alt-text-manager"
  | "welcome-page-access"
  | "inventory-manager-page-access"
  | "ddc-configuration-access"
  | "deptsvc.department-management.enable"
  | "css.dashboard-overview.audit-history"
  | "ddc.nrt-database-filter.show"
  | "ddc.nrt-provider-filter.show"
  | "ddc.oem-compliance-center.enable";

export type InventoryFlagNames =
  | "ccinv-is-out-of-staging-action"
  | "ccinv-is-threshold-feature-active"
  | "ccinv-lc-nonzero-values-allowed"
  | "allow-incentives-actions-on-lifecycles"
  | "vdp-media-edit-disabled"
  // Pre-dates the monorepo: legacy cc-inventory's `isEnableAllVehicleStatus` reads it
  // to decide whether the In Transit At Port/Sold/At Factory statuses are offered.
  | "cc-inventory-all-vehicle-status-toggle";

export type AutotraderFlagNames =
  | "autotrader.testflag"
  | "autotrader.new-merchandising-navbar-menu.enable"
  | "autotrader.garage-itool.enable"
  | "autotrader.garage-cic.enable"
  | "autotrader.garage-dealer-inv.enable"
  | "autotrader.garage-lead.enable"
  | "autotrader.garage-fraud-tools.enable"
  | "autotrader.fee-manager.enable"
  | "autotrader.fee-manager.use-common-org-id"
  | "autotrader.dealer-preferences.enable"
  | "autotrader.order-manager.enable"
  | "autotrader.audit-history.enable"
  | "autotrader.account-contract.enable"
  | "autotrader.dba-names.enable"
  | "autotrader.graphics-library.enable"
  | "autotrader.account.search.enable"
  | "autotrader.customer-master.enable"
  // JSON-valued (not boolean) — must be accessed through useManageAccountSideNavFlag,
  // not useAutotraderFlags(), which types every AutotraderFlagName as boolean.
  | "dealercenter-manage-account-sidenavs";

export type TestFlagNames = "devTestFlag";
export type GenericFlags<TFlagNames extends string> = Partial<
  Record<TFlagNames, boolean>
>;

export type AllFlagsNames =
  | TestFlagNames
  | CC9FlagNames
  | AutotraderFlagNames
  | InventoryFlagNames;

type AppNames = "test" | "cc9" | "autotrader";

type Flags = { name: AllFlagsNames; apps: AppNames[]; description: string }[];

const FLAGS: Flags = [
  {
    name: "cc9.website-inventory-config.show-bulk-import",
    apps: ["cc9"],
    description:
      "Enables the bulk import button on the website inventory config page"
  },
  {
    name: "css.enable_dealer_code_service_use",
    apps: ["cc9"],
    description:
      "Was used to enable new api for dealer codes. This needs to be removed"
  },
  {
    name: "cc9-client-search.show-download",
    apps: ["cc9"],
    description: "Shows download button in account search"
  },
  /** Mock flag only for test page */
  {
    name: "devTestFlag",
    apps: ["test"],
    description: "Test flag for testing purposes"
  },
  {
    name: "cc9.test.testflag",
    apps: ["cc9"],
    description:
      "Test flag for testing purposes. Feels like we shouldn't need two of these"
  },
  {
    name: "cc9.account-copy.show",
    apps: ["cc9"],
    description: "Adds media library copy link to navigation"
  },
  {
    name: "cc9.user-permissions-from-group-resource",
    apps: ["cc9"],
    description:
      "Gets userPermissionsFromGroupResource property on the resources get fetch call"
  },
  {
    name: "cc9.resources-hide-ui",
    apps: ["cc9"],
    description: "Hides the 'Inherit from parent' menu button on resouces page"
  },
  {
    name: "cc9.get-legacy-data",
    apps: ["cc9"],
    description: "Gets legacy data from the resources get fetch call"
  },
  {
    name: "autotrader.testflag",
    apps: ["autotrader"],
    description: "Test flag for testing purposes"
  },
  {
    name: "autotrader.new-merchandising-navbar-menu.enable",
    apps: ["autotrader"],
    description:
      "Enable renders new DealerCenter navbar with DealerCenter URLs for merchandising pages"
  },
  {
    name: "autotrader.fee-manager.enable",
    apps: ["autotrader"],
    description: "Controls access to the Autotrader fee manager page"
  },
  {
    name: "autotrader.fee-manager.use-common-org-id",
    apps: ["autotrader"],
    description:
      "Shows the internal-only common org fee source switch in the Autotrader fee manager configuration modal"
  },
  {
    name: "autotrader.dealer-preferences.enable",
    apps: ["autotrader"],
    description:
      "Enables the Dealer Preferences page for Autotrader dealers (Fastlane migration)"
  },
  {
    name: "autotrader.account-contract.enable",
    apps: ["autotrader"],
    description:
      "Enables the Account Contract page for Autotrader dealers (Fastlane migration)"
  },
  {
    name: "autotrader.dba-names.enable",
    apps: ["autotrader"],
    description: "Enables the DBA Names management page in DealerCenter"
  },
  {
    name: "autotrader.order-manager.enable",
    apps: ["autotrader"],
    description: "Enables the Order Manager page for Autotrader dealers"
  },
  {
    name: "autotrader.audit-history.enable",
    apps: ["autotrader"],
    description:
      "Enables the Audit History page for Autotrader dealers (Fastlane migration)"
  },
  {
    name: "autotrader.customer-master.enable",
    apps: ["autotrader"],
    description:
      "Enables the Customer Master page for Autotrader dealers (Fastlane migration)"
  },
  {
    name: "autotrader.graphics-library.enable",
    apps: ["autotrader"],
    description:
      "Enables the Graphic Library page for Autotrader Dealer (Fastlane migration)"
  },
  {
    name: "autotrader.account.search.enable",
    apps: ["autotrader"],
    description:
      "Enables the multi-type Account Search experience (search type selector + Search Result table). When false, the legacy plain Dealer-ID search bar renders unchanged."
  },
  {
    name: "cc9.user-permissions-manager.enable",
    apps: ["cc9"],
    description: "Show user permissions manager page"
  },
  {
    name: "incentives-manager-access",
    apps: ["cc9"],
    description: "Enables access to the incentives manager page"
  },
  {
    name: "cc9.media-library-copy.enable",
    apps: ["cc9"],
    description: "Use CC9 Media Library Copy endpoint"
  },
  {
    name: "css.franchise-management.enable",
    apps: ["cc9"],
    description: "Enables Franchise Management features"
  },
  {
    name: "cis.account-creation.enable",
    apps: ["cc9"],
    description: "Enables Account Creation features"
  },
  {
    name: "alt-text-manager",
    apps: ["cc9"],
    description: "Enables the Alt Text Manager tab"
  },
  {
    name: "deptsvc.department-management.enable",
    apps: ["cc9"],
    description:
      "Enables the new department service endpoints for CRUD operations and database. Controls add, edit, and delete buttons for DDC dealer departments"
  },
  {
    name: "welcome-page-access",
    apps: ["cc9"],
    description: "Controls access to the welcome page"
  },
  {
    name: "inventory-manager-page-access",
    apps: ["cc9"],
    description: "Controls access to the inventory manager page"
  },
  {
    name: "ddc-configuration-access",
    apps: ["cc9"],
    description: "Controls access to the DDC configuration pages"
  },
  {
    name: "css.dashboard-overview.audit-history",
    apps: ["cc9"],
    description: "Shows the Audit History button on the Dashboard Overview tab"
  },
  {
    name: "autotrader.garage-itool.enable",
    apps: ["autotrader"],
    description: "Enables the iTool module in CAI Garage"
  },
  {
    name: "autotrader.garage-cic.enable",
    apps: ["autotrader"],
    description: "Enables the CIC module in CAI Garage"
  },
  {
    name: "autotrader.garage-dealer-inv.enable",
    apps: ["autotrader"],
    description: "Enables the Dealer Inventory module in CAI Garage"
  },
  {
    name: "autotrader.garage-lead.enable",
    apps: ["autotrader"],
    description: "Enables the Lead module in CAI Garage"
  },
  {
    name: "autotrader.garage-fraud-tools.enable",
    apps: ["autotrader"],
    description: "Enables the Fraud Tools module in CAI Garage (P5)"
  },
  {
    name: "dealercenter-manage-account-sidenavs",
    apps: ["autotrader"],
    description:
      "JSON-valued flag (not boolean): per-link { enabled: boolean } entries controlling DealerCenter redirects for Manage Account side-nav links"
  },
  {
    name: "ddc.oem-compliance-center.enable",
    apps: ["cc9"],
    description:
      "Shows the OEM Compliance Center item in the INTERNAL nav and enables its route"
  }
];

// Flags that default to false when LD doesn't return a value (fail-closed for mid-rollout features).
// All other flags default to true. This is the safety net when LD is unavailable or a flag is
// not yet provisioned in that environment — the actual on/off control is in the LD dashboard.
const OFF_BY_DEFAULT: AllFlagsNames[] = [
  "autotrader.garage-itool.enable",
  "autotrader.garage-cic.enable",
  "autotrader.garage-dealer-inv.enable",
  "autotrader.garage-lead.enable",
  "autotrader.garage-fraud-tools.enable",
  "autotrader.fee-manager.use-common-org-id",
  "ddc.oem-compliance-center.enable"
];

export const getActiveFlags = (appName: AppNames) => {
  return FLAGS.reduce((acc, { name, apps }) => {
    if (apps.some(app => app === appName)) {
      const isOffByDefault = OFF_BY_DEFAULT.includes(name);
      return {
        ...acc,
        [name]: isOffByDefault ? false : true
      };
    } else {
      return acc;
    }
  }, {});
};
